/**
 * Sales channels and the life of an order that arrives through one.
 *
 * A restaurant sells the same food three ways: across the counter, through an
 * aggregator like Swiggy or Zomato, and direct to a customer who rang up or
 * scanned the QR code on the table. The kitchen does not care which - the food
 * is the same and so is the stock it consumes - but the money and the tracking
 * are completely different, and that is what lives here.
 */
import { pctOf } from './money.js';
export const ORDER_CHANNELS = ['IN_HOUSE', 'SWIGGY', 'ZOMATO', 'PHONE', 'WEB'];
export const CHANNEL_LABELS = {
    IN_HOUSE: 'In house',
    SWIGGY: 'Swiggy',
    ZOMATO: 'Zomato',
    PHONE: 'Phone',
    WEB: 'Own online menu',
};
/** Channels that arrive from outside and need tracking rather than a table. */
export const ONLINE_CHANNELS = ['SWIGGY', 'ZOMATO', 'PHONE', 'WEB'];
export const isOnlineChannel = (channel) => ONLINE_CHANNELS.includes(channel);
/**
 * Channels where somebody else owns the customer and takes a cut.
 *
 * The distinction matters for money, not for cooking: an aggregator collects
 * from the diner and pays the restaurant later, less commission. A phone order
 * or one placed on the hotel's own menu is paid straight to the hotel.
 */
export const isAggregatorChannel = (channel) => channel === 'SWIGGY' || channel === 'ZOMATO';
/* ---------------- fulfilment ---------------- */
export const FULFILMENT_STATUSES = [
    'NEW',
    'ACCEPTED',
    'PREPARING',
    'READY',
    'DISPATCHED',
    'DELIVERED',
    'CANCELLED',
    'REJECTED',
];
export const FULFILMENT_LABELS = {
    NEW: 'New',
    ACCEPTED: 'Accepted',
    PREPARING: 'In the kitchen',
    READY: 'Ready',
    DISPATCHED: 'Out for delivery',
    DELIVERED: 'Delivered',
    CANCELLED: 'Cancelled',
    REJECTED: 'Rejected',
};
/**
 * Where an order can go next.
 *
 * Forwards only, with two exits. An order can be cancelled up to the moment it
 * leaves the building, because a customer changes their mind and a rider never
 * turns up - but nothing comes back from DELIVERED, and REJECTED is the answer
 * to a brand new order the kitchen cannot take, not a way out of one it has
 * already started cooking.
 */
const TRANSITIONS = {
    NEW: ['ACCEPTED', 'REJECTED', 'CANCELLED'],
    ACCEPTED: ['PREPARING', 'CANCELLED'],
    PREPARING: ['READY', 'CANCELLED'],
    // A collected takeaway is delivered without ever being dispatched.
    READY: ['DISPATCHED', 'DELIVERED', 'CANCELLED'],
    DISPATCHED: ['DELIVERED'],
    DELIVERED: [],
    CANCELLED: [],
    REJECTED: [],
};
export const nextFulfilmentStatuses = (from) => TRANSITIONS[from] ?? [];
export const canTransitionFulfilment = (from, to) => nextFulfilmentStatuses(from).includes(to);
/** An order nobody can move any further. */
export const isFulfilmentFinal = (status) => TRANSITIONS[status].length === 0;
/** Delivered is the only ending where food was sold and money is owed. */
export const isFulfilmentFulfilled = (status) => status === 'DELIVERED';
/* ---------------- aggregator money ---------------- */
/** GST an aggregator charges the restaurant on its own commission. */
export const COMMISSION_GST_RATE = 18;
/**
 * What an aggregator order is really worth to the restaurant.
 *
 * The headline is the bill; the money is the payout. Commission is charged on
 * the net food value rather than the taxed total - that is the base Swiggy and
 * Zomato both use - and the aggregator charges GST on its commission because
 * the commission is a service it sold you.
 *
 * This is deliberately not the full settlement: aggregators also net off
 * delivery fees they absorbed, promotional discounts they funded, TCS and TDS.
 * Those vary per contract and per month, so they belong in a reconciliation
 * against a real payout statement, not in an estimate at order time.
 */
export function aggregatorPayout(input) {
    const { subtotal, total, commissionPct, commissionGstRate = COMMISSION_GST_RATE } = input;
    const rate = Math.min(100, Math.max(0, commissionPct));
    const commission = pctOf(subtotal, rate);
    const commissionGst = pctOf(commission, commissionGstRate);
    return { commission, commissionGst, payout: total - commission - commissionGst };
}
/* ---------------- client addresses ---------------- */
/**
 * A bare IP address from whatever a proxy actually sent.
 *
 * Behind IIS and ARR — the usual Windows deployment — `X-Forwarded-For` carries
 * the client port as well: `49.43.247.106:60438`. Express hands that whole
 * string back as `req.ip`, and a rate limiter that expects an address rejects
 * it outright, taking the endpoint down with it. Nginx does not do this, which
 * is why it only shows up once somebody deploys behind IIS.
 *
 * Ports are stripped, IPv6 is left intact (its colons are part of the address,
 * not a port), and anything unrecognisable becomes a stable fallback so that
 * requests are still limited rather than waved through.
 */
export function normaliseClientIp(value) {
    const raw = (value ?? '').split(',')[0]?.trim() ?? '';
    if (!raw)
        return 'unknown';
    // [2001:db8::1]:443 — the bracketed form is unambiguous.
    const bracketed = /^\[([^\]]+)\](?::\d+)?$/.exec(raw);
    if (bracketed)
        return bracketed[1].toLowerCase();
    // ::ffff:1.2.3.4 is an IPv4 address wearing an IPv6 coat.
    const mapped = /^::ffff:(\d{1,3}(?:\.\d{1,3}){3})$/i.exec(raw);
    if (mapped)
        return mapped[1];
    // 1.2.3.4:5678 — exactly one colon and a v4 address in front of it.
    const withPort = /^(\d{1,3}(?:\.\d{1,3}){3}):\d+$/.exec(raw);
    if (withPort)
        return withPort[1];
    // A bare IPv6 address has several colons and no port to remove.
    if (raw.includes(':') && raw.split(':').length > 2)
        return raw.toLowerCase();
    return raw;
}
//# sourceMappingURL=channels.js.map